import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Mail, ArrowLeft, Loader2, ArrowRight, MailCheck, Clock } from "lucide-react";
import AuthHero from "@/components/AuthHero";
import axios from "axios";
import "@/styles/landing.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success("Password reset link sent! Check your email.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 404) {
        toast.error(detail || "No account found with this email.", {
          action: { label: "Create account", onClick: () => navigate("/auth?mode=signup") },
        });
      } else {
        toast.error(detail || "Failed to send reset link — please try again");
      }
    } finally {
      setLoading(false);
    }
  };

  const inputBase = "pl-11 h-12 rounded-xl bg-white border border-[var(--ap-line)] text-[var(--ap-ink)] placeholder:text-gray-400 focus:border-[var(--ap-violet)] focus:ring-2 focus:ring-[var(--ap-violet-soft)] transition-colors";

  return (
    <div className="ap-root min-h-screen flex" data-testid="forgot-password-page">
      {/* ── Brand hero (desktop) ──────────────────────────────────────────── */}
      <AuthHero />

      {/* ── Form panel ────────────────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center px-5 py-10" style={{ background: "var(--ap-canvas)" }}>
        <div className="w-full max-w-[420px] ap-rise">
          {/* mobile brand */}
          <button onClick={() => navigate("/")} className="lg:hidden bg-transparent border-0 cursor-pointer p-0 mb-7 block" aria-label="PlacementOS home">
            <img src="/logo.png" alt="PlacementOS" className="h-9 w-auto" />
          </button>

          <button
            onClick={() => navigate("/auth")}
            className="inline-flex items-center gap-1.5 text-[var(--ap-muted)] hover:text-[var(--ap-ink)] mb-7 transition-colors bg-transparent border-0 cursor-pointer text-sm"
            data-testid="back-to-signin"
          >
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </button>

          <div className="w-14 h-14 rounded-2xl grid place-items-center mb-5 shadow-[var(--ap-glow)]" style={{ background: sent ? "rgba(22,163,74,.12)" : "var(--ap-violet-soft)" }}>
            {sent
              ? <MailCheck className="w-7 h-7 text-[var(--ap-green)]" />
              : <Mail className="w-7 h-7 text-[var(--ap-violet)]" />}
          </div>

          <h2 className="ap-display text-[28px] leading-tight font-extrabold text-[var(--ap-ink)]">
            {sent ? "Check your email" : "Forgot password?"}
          </h2>
          <p className="text-[var(--ap-muted)] text-sm mt-1.5 mb-6">
            {sent
              ? <>We've sent a reset link to <span className="font-semibold text-[var(--ap-ink)]">{email}</span>.</>
              : "Enter your account email and we'll send you a link to reset your password."}
          </p>

          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ap-muted)] z-10" />
                <Input
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className={inputBase}
                  data-testid="forgot-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="ap-btn ap-btn-fire ap-btn-lg w-full disabled:opacity-60"
                data-testid="send-reset-link-btn"
              >
                {loading
                  ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>)
                  : (<>Send reset link <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="ap-card p-4 flex items-start gap-3">
                <MailCheck className="w-5 h-5 text-[var(--ap-green)] shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-[var(--ap-ink)]">Email sent</p>
                  <p className="text-xs text-[var(--ap-muted)] mt-0.5">Check your inbox — and your spam folder, just in case.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-[var(--ap-muted)] ml-1">
                <Clock className="w-3.5 h-3.5" /> The link expires in 1 hour.
              </div>
              <button
                onClick={() => navigate("/auth")}
                className="ap-btn ap-btn-ghost ap-btn-lg w-full"
                data-testid="back-to-signin-btn"
              >
                Back to sign in
              </button>
              {/* Only meaningful AFTER an email was sent — resend goes back to the form. */}
              <p className="text-center text-xs text-[var(--ap-muted)] pt-1">
                Didn't get it? Check spam, or{" "}
                <button onClick={() => !loading && setSent(false)} className="text-[var(--ap-violet)] font-semibold hover:underline bg-transparent border-0 cursor-pointer p-0">
                  send it again
                </button>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
